import { EventBody, EventResponse, SubEventBody, Question } from '../../gds-api-client';
import RightRail, { Route, RouterProvider } from '../../gs-component-right-rail';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import isempty from 'lodash/isEmpty';
import get from 'lodash/get';
import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { ApiClientsContext, EventType } from '../components/ApiClientsContext';
import { DeleteConfirmation } from '../components/DeleteConfirmation';
import { EditConfirmation } from '../components/EditConfirmation';
import { ErrorContext } from '../components/ErrorContext';
import { LocationContext } from '../components/LocationContext';
import { MemberContext } from '../components/MemberContext';
import { AnalyticsProps, TrackingContext } from '../components/TrackingContext';
import { getBookingBody } from '../helpers/getBookingBody';
import { populateSubEvents } from '../helpers/populateSubEvent';
import addMealQuestionToQuestions from '../helpers/addMealQuestionToQuestions';
import { getLocation } from '../helpers/getLocation';
import { OnSuccess } from '../LegacyModal/RightRailForm';
import { CustomVendorPage } from './CustomVendorPage';
import { MainScreen } from './MainScreen';
import { ScheduleItem } from './ScheduleItem';
import { emptyMealQuestion, emptyEvent } from './helpers';
import { AutoFocusableFields } from './interface';

export type SoEInitialValues = Pick<
  EventBody,
  'name' | 'attire' | 'notes' | 'time' | 'endTime' | 'visible' | 'location' | 'date' | 'requireRsvp'
> & {
  subEvents: SubEventBody[];
  hideDate: boolean;
  mealQuestion: Question;
};

export interface SoEFormProps {
  open: boolean;
  setClose: (value: boolean) => void;
  weddingId: string;
  type: EventType;
  event: EventBody | null;
  hasWWS: boolean;
  onSuccess: OnSuccess;
  hideDate?: boolean | null;
  onHideDate: (hideDate: boolean) => void;
  autoFillEventProperties?: EventBody;
  onEscapeKey?: () => void;
  autoFocusField?: AutoFocusableFields | undefined;
  isExperiment?: boolean;
  preview?: boolean;
  overlay?: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Event Name is required'),
  subEvents: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Schedule Event Title is required'),
      time: Yup.string().when('type', {
        is: (type): boolean => type === 'WeddingEvent',
        then: Yup.string()
          .matches(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/, {
            message: 'Schedule Event Time is in wrong format',
          })
          .required('Schedule Event Time is required'),
      }),
    })
  ),
});

const getEventData = (data: EventResponse, source?: string): AnalyticsProps => ({
  source: source || 'event panel',
  eventName: data.name,
  eventType: data.type === 'WeddingEvent' ? 'custom' : 'default', // should always be custom
  eventVisible: data.visible,
  rsvpOn: data.requireRsvp,
  eventScheduleItems: data.subEvents ? data.subEvents.length : 0,
});

export const ScheduleOfEventsForm: React.FC<SoEFormProps> = ({
  open,
  setClose,
  event: gdsEvent,
  type,
  hasWWS,
  weddingId,
  hideDate,
  autoFillEventProperties = {},
  onSuccess,
  onHideDate,
  onEscapeKey,
  autoFocusField,
  isExperiment,
  preview = false,
  overlay = true,
}) => {
  const [loading, setLoading] = useState(false);
  const init: SoEInitialValues = React.useMemo(() => {
    return {
      ...emptyEvent,
      mealQuestion: emptyMealQuestion(),
      requireRsvp: false,
      hideDate: Boolean(hideDate),
      ...autoFillEventProperties,
    };
  }, [hideDate, autoFillEventProperties]);

  const [initialValues, setInitialValues] = useState(init);
  const isNew = !gdsEvent;
  const { setGdsEvent, setEventType, weddingApi, bookingsSource = '', gdsApi, memberWeddingApi } = useContext(
    ApiClientsContext
  );
  const { member: { id: memberId = '' } = {} } = useContext(MemberContext);
  const { setErrors, clearError } = useContext(ErrorContext);
  const { location: locationStore, locationsSaved, setLocationsSaved } = useContext(LocationContext);
  const { track } = useContext(TrackingContext);
  useEffect(() => {
    setGdsEvent(gdsEvent);
  }, [gdsEvent, setGdsEvent]);

  useEffect(() => {
    setEventType(type);
  }, [setEventType, type]);

  const populateSubEventsCallback = React.useCallback(
    () => populateSubEvents(get(gdsEvent, 'subEvents') || [], locationStore),
    [gdsEvent, locationStore]
  );

  useEffect(() => {
    if (gdsEvent) {
      // cannot rely need to consider null values
      const { name, attire, date, time, endTime, requireRsvp, visible, notes, location, questions } = gdsEvent;
      const mealQuestion = questions ? questions.find(question => question.type === 'Meal') : undefined;

      setInitialValues({
        name: name || '',
        attire: attire || '',
        mealQuestion: mealQuestion || emptyMealQuestion(),
        date: date || '',
        time: time || '',
        endTime: endTime || '',
        notes: notes || '',
        requireRsvp: requireRsvp || false,
        visible: visible === null ? true : visible,
        subEvents: populateSubEventsCallback(),
        location: getLocation(location || {}),
        hideDate: Boolean(hideDate),
      });
    } else {
      setInitialValues(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gdsEvent, hideDate, init]);

  // @ts-ignore
  const onSuccessfulSave = (data, resetForm): void => {
    const successData = {
      ...data,
      location: data.location || null,
    };
    onSuccess(successData);
    setLoading(false);
    clearError();
    resetForm();
    setClose(false);
  };

  const onSubmit = async (
    formValues: SoEInitialValues,
    { resetForm }: FormikActions<SoEInitialValues>
  ): Promise<void> => {
    setLoading(true);
    const { hideDate: hideDateValue, ...values } = formValues;
    const questions = Array.isArray(gdsEvent) ? get(gdsEvent[0], ['questions']) : get(gdsEvent, ['questions']);

    try {
      if (isNew) {
        const { mealQuestion, ...rest } = values;
        const newEvent: EventBody = { ...rest };

        const mealOptions = get(mealQuestion, 'options', []);
        const optionsWithText = mealOptions.filter(o => o && o.text && o.text.trim());
        if (get(optionsWithText, 'length')) {
          newEvent.questions = [{ ...mealQuestion, options: optionsWithText }];
        }

        const { data } = await gdsApi.createEvent(weddingId, newEvent);
        track('Event Added', { ...getEventData(data) });

        onSuccessfulSave(data, resetForm);
        return;
      }

      if (gdsEvent) {
        if (type === 'WeddingDay') {
          const { date, subEvents, mealQuestion, ...rest } = values;
          let weddingDate: string | null = null;
          // Store WeddingDay date to wedding api
          if (date) {
            const weddingResponse = await weddingApi.upsertWedding({
              wedding: {
                member_id: memberId,
                wedding_date: date,
              },
            });
            weddingDate = weddingResponse.wedding.wedding_date;
          }

          // Store hideDate to memberWedding
          const {
            data: {
              data: { updateWeddingWebsite },
            },
          } = await memberWeddingApi.updateMemberWedding({
            hideDate: hideDateValue,
          });

          onHideDate(updateWeddingWebsite.hideDate);

          // If subevents & ceremony & reception store to bookings
          const ceremony = subEvents.find(e => e.type === 'Ceremony');
          const reception = subEvents.find(e => e.type === 'Reception');

          if (ceremony && locationStore.CER && !isempty(locationStore.CER.address_components)) {
            const { booking: upsertedBooking } = await weddingApi.upsertBooking({
              booking: getBookingBody({
                ...locationStore.CER,
                category_code: 'CER',
                memberId,
                bookingsSource,
              }),
            });

            if (!locationsSaved.CER) {
              // Send a "booking-created" analytics event
              track('Booking Created', {
                source: 'event-modal',
                booking_id: upsertedBooking.id,
                wedding_id: weddingId,
                member_id: memberId,
                event_id: gdsEvent.id,
              });
            }

            setLocationsSaved({
              CER: true,
              REC: locationsSaved.REC,
            });
          }

          if (reception && locationStore.REC && !isempty(locationStore.REC.address_components)) {
            // sends reception and ceremony subevents to bookings
            const { booking: upsertedBooking } = await weddingApi.upsertBooking({
              booking: getBookingBody({
                ...locationStore.REC,
                category_code: 'REC',
                memberId,
                bookingsSource,
              }),
            });

            if (!locationsSaved.REC) {
              // Send a "booking-created" analytics event
              track('Booking Created', {
                source: 'event-modal',
                booking_id: upsertedBooking.id,
                wedding_id: weddingId,
                member_id: memberId,
                event_id: gdsEvent.id,
              });
            }

            setLocationsSaved({
              CER: locationsSaved.CER,
              REC: true,
            });
          } else if (!locationStore.REC && !locationStore.CER && locationStore.WEDDING) {
            // send to bookings when ceremony and reception is empty for wedding vision.
            const { booking: upsertedBooking } = await weddingApi.upsertBooking({
              booking: getBookingBody({
                ...locationStore.WEDDING,
                category_code: 'REC',
                memberId,
                bookingsSource,
              }),
            });

            if (!locationsSaved.REC) {
              // Send a "booking-created" analytics event
              track('Booking Created', {
                source: 'event-modal',
                booking_id: upsertedBooking.id,
                wedding_id: weddingId,
                member_id: memberId,
                event_id: gdsEvent.id,
              });
            }

            setLocationsSaved({
              CER: locationsSaved.CER,
              REC: true,
            });
          }

          // rest save to GDS
          const { data } = await gdsApi.putEvent(gdsEvent.id || '', {
            ...rest,
            questions: addMealQuestionToQuestions(questions, mealQuestion),
            type: gdsEvent.type || 'WeddingEvent',
            weddingId,
            subEvents,
          });

          const gdsData = {
            ...data,
            date: weddingDate || data.date,
          };

          track('Event Updated', {
            ...getEventData(gdsData, 'event modal'),
          });
          onSuccessfulSave(gdsData, resetForm);
        } else {
          const { mealQuestion, ...body } = values;
          const { data } = await gdsApi.putEvent(gdsEvent.id || '', {
            ...body,
            weddingId,
            questions: addMealQuestionToQuestions(questions, mealQuestion),
            type: gdsEvent.type || 'WeddingEvent',
          });
          track('Event Updated', {
            ...getEventData(data, 'event modal'),
          });
          onSuccessfulSave(data, resetForm);
        }
      }
    } catch (error) {
      setErrors();
      setLoading(false);
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {(formProps): JSX.Element => (
        <RightRail open={open} onEscapeKey={onEscapeKey} overlay={overlay}>
          <RouterProvider entry="main">
            <Route
              screen="main"
              component={
                <MainScreen
                  isNew={isNew}
                  hasWWS={hasWWS}
                  formProps={formProps}
                  setClose={setClose}
                  loading={loading}
                  autoFocusField={autoFocusField}
                  isExperiment={isExperiment}
                  preview={preview}
                />
              }
            />
            <Route screen="scheduleItem" component={<ScheduleItem formProps={formProps} />} />
            <Route
              screen="confirmation"
              component={
                <EditConfirmation
                  setClose={setClose}
                  resetForm={formProps.resetForm}
                  submitForm={formProps.submitForm}
                />
              }
            />
            <Route
              screen="deleteConfirmation"
              component={<DeleteConfirmation setClose={setClose} onSuccess={onSuccess} />}
            />
            <Route screen="customVendor" component={<CustomVendorPage formProps={formProps} />} />
          </RouterProvider>
        </RightRail>
      )}
    </Formik>
  );
};
